import { useCallback, useRef, useState } from 'react';

import { StatusBanner } from '@xing-com/banner';
import { IconVideo } from '@xing-com/icons';

import { ACCEPTED_VIDEO_FILES, MAX_VIDEO_SIZE } from '../../../constants';
import * as Styled from '../styles';
import type { VideoToolProps } from '../types';

export const isValidVideoFileSize = (file: File): boolean => {
  const maxAllowedSize = MAX_VIDEO_SIZE * 1024 * 1024;
  return file.size <= maxAllowedSize;
};

export const VideoTool: React.FC<VideoToolProps> = ({
  editor,
  disabled,
  onVideoUpload,
}) => {
  const [error, setError] = useState<string | undefined>();
  const inputFileRef = useRef<HTMLInputElement | null>(null);

  const handleVideoUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!editor || !e.target.files?.length) {
        return;
      }

      const file = e.target.files.item(0);
      if (!file) {
        setError(`Error selecting the file.`);
        return;
      }

      if (!isValidVideoFileSize(file)) {
        setError(`File size exceeds the maximum limit of ${MAX_VIDEO_SIZE}MB.`);
        return;
      }

      const { error, videoId } = await onVideoUpload(file);
      if (error || !videoId) {
        setError('Error uploading the video.');
        return;
      }

      editor.chain().focus().addXingVideo(videoId).createParagraphNear().run();

      e.target.value = '';
      return;
    },
    [editor, onVideoUpload]
  );

  return (
    <div>
      <Styled.ToolBarButton
        onClick={() => {
          inputFileRef.current?.click?.();
        }}
        disabled={disabled}
      >
        <IconVideo width={24} height={24} />
      </Styled.ToolBarButton>
      <input
        ref={inputFileRef}
        data-qa="articles-video-tool-input"
        data-testid="articles-video-tool-input"
        type="file"
        accept={ACCEPTED_VIDEO_FILES}
        onChange={handleVideoUpload}
        hidden
      />
      <StatusBanner display="fixed" show={!!error} variant="error">
        {error}
      </StatusBanner>
    </div>
  );
};
